import { useState, useEffect, useMemo } from 'react';
import getNestedValue from '@/utils/getNestedValue';
import type { ChangeEvent } from 'react';
import type { NestedKeys } from '@/types/util.types';

interface UseSimpleSearchOptions<T> {
  data: T[];
  searchableFields: NestedKeys<T>[];
  debounceMs?: number;
}

/**
 * A custom hook for simple search functionality with support for nested fields
 * @param options Search configuration options
 * @returns Object containing search state and filtered results
 */
function useSimpleSearch<T>({ data, searchableFields, debounceMs = 300 }: UseSimpleSearchOptions<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    setIsSearching(true);
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setIsSearching(false);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchTerm, debounceMs]);

  const filteredResults = useMemo(() => {
    if (!debouncedSearchTerm) return data;

    const lowerCaseSearch = debouncedSearchTerm.toLowerCase();

    return data.filter((item) => {
      return searchableFields.some((field) => {
        const value = getNestedValue(item, field);
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(lowerCaseSearch);
      });
    });
  }, [data, debouncedSearchTerm, searchableFields]);

  function handleSearchChange(event: ChangeEvent<HTMLInputElement>) {
    setSearchTerm(event.target.value);
  }

  function clearSearch() {
    setSearchTerm('');
  }

  return {
    searchTerm,
    setSearchTerm,
    handleSearchChange,
    clearSearch,
    filteredResults,
    isSearching,
  };
}

export default useSimpleSearch;
